import React, { useState, useRef } from 'react';
import emailjs from '@emailjs/browser';
import './Form.scss';
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/material.css'
import { 
  TextField, 
  Button, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Typography, 
  Box, 
  Grid, 
  Paper,
  Checkbox,
  FormControlLabel,
  createTheme,
  ThemeProvider
} from '@mui/material';
import { styled } from '@mui/system';

// Create a custom theme with the specified colors
const theme = createTheme({
  palette: {
    primary: { main: '#ff551a' },
    secondary: { main: '#341f71' },
    warning: { main: '#eaaf3b' },
    success: { main: '#4caf50' },
    error: { main: '#f44336' },
    grey: { main: '#9e9e9e' },
  },
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ff551a' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#ff551a' },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          '&.Mui-focused': { color: '#ff551a' },
        },
      },
    },
  },
});

const StyledPaper = styled(Paper)(({ theme }) => ({
  maxWidth: '42rem',
  margin: '0 auto',
  padding: theme.spacing(4),
  [theme.breakpoints.up('md')]: { padding: theme.spacing(6) },
}));

const RequiredLabel = styled('span')({
  color: 'red',
  marginLeft: '4px',
});

export default function ConsultationForm({ defaultPlan = '' }) {
  const [formData, setFormData] = useState({
    name: '',
    user_name: '',
    contact_number: '',
    user_email: '',
    reason_consultation: '',
    plan: defaultPlan, 
    consultation_mode: ''
  });

  const [presencialCheck, setPresencialCheck] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const form = useRef();

  const handleChange = (e) => {
    const { name, value } = e.target;
  
    setFormData((prevState) => {
      const updatedFormData = { ...prevState, [name]: value };

      if (name === "plan" && value === "medidas") {
        updatedFormData.consultation_mode = "presencial";
        setPresencialCheck(false);
      }
      return updatedFormData;
    });
  };

  const handlePresencialCheck = (e) => {
    const checked = e.target.checked;
    setPresencialCheck(checked);

    setFormData((prev) => ({
      ...prev,
      consultation_mode: checked ? "presencial" : prev.consultation_mode,
      anthropometric_measures: checked ? "sí" : "no"
    }));
  };

  const sendEmail = (e) => {
    e.preventDefault();

    if (!formData.contact_number || formData.contact_number.replace(/\D/g, "").length <= 3) {
      setMessage({ text: 'Por favor, ingresa un número de contacto válido.', type: 'error' });
      return;
    }

    emailjs
      .sendForm('service_5xjce2b', 'template_an12kfb', form.current, {
        publicKey: 'kZZkcyvdB31gWkPKA',
      })
      .then(
        () => {
          setMessage({ text: '¡Tu reserva fue generada con éxito, me comunicaré contigo para agendar la consulta!', type: 'success' });
          setFormData({
            name: '',
            user_name: '',
            contact_number: '',
            user_email: '',
            reason_consultation: '',
            plan: '',
            consultation_mode: '',
            anthropometric_measures: 'no'
          });
          setPresencialCheck(false);
        },
        (error) => {
          setMessage({ text: 'Error al enviar la consulta. Intenta nuevamente.', type: 'error' });
          console.log('FAILED...', error.text);
        },
      );
  };

  return (
    <ThemeProvider theme={theme}>
      <StyledPaper elevation={3}>
        <Typography variant="h1" component="h4" gutterBottom color="secondary" fontWeight="500" 
          sx={{ fontSize: '32px', marginBottom: '2rem', textAlign: 'center', fontFamily: 'Lora, sans-serif' }}>
          ¡Agendemos una Consulta!
        </Typography>
        
        <Typography variant="p" component="p" gutterBottom color="grey" fontWeight="500" 
          sx={{ fontSize: '16px', marginBottom: '0.5rem', textAlign: 'left' }}>
          Si llegaste hasta acá, es porque estás lista para dejar de hacer dietas que no funcionan
        </Typography>
        
        <Typography variant="p" component="p" gutterBottom color="grey" fontWeight="500" 
          sx={{ fontSize: '16px', marginBottom: '3rem', textAlign: 'left' }}>
          Completa el formulario y en las próximas 24 horas te estaré contactando para que iniciemos tu proceso de transformación
        </Typography>

        {/* FORM */}
        <form ref={form} onSubmit={sendEmail}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                label="Nombre Completo"
                name="user_name"
                value={formData.user_name}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel shrink style={{ backgroundColor: '#fff' }}>Número de Contacto</InputLabel>
                <PhoneInput
                  required
                  country={'uy'}
                  value={formData.contact_number}
                  onChange={(phone) =>
                    setFormData({ ...formData, contact_number: phone })
                  }
                  inputProps={{
                    name: 'contact_number',
                    required: true,
                  }}
                  placeholder="Ej: 91234567"
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Correo Electrónico"
                name="user_email"
                type="email"
                value={formData.user_email}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                multiline
                rows={4}
                label="Razón de la Consulta"
                name="reason_consultation"
                value={formData.reason_consultation}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel shrink>Plan</InputLabel>
                <Select
                  name="plan"
                  value={formData.plan}
                  onChange={handleChange}
                  label="Plan"
                  displayEmpty
                >
                  <MenuItem value="" disabled>Elige un Plan</MenuItem>
                  <MenuItem value="mensual">Mensual</MenuItem>
                  <MenuItem value="trimestral">Trimestral</MenuItem>
                  <MenuItem value="semestral">Semestral</MenuItem>
                  <MenuItem value="medidas">Medidas Antropométricas</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel shrink>Modalidad</InputLabel>
              <Select
                name="consultation_mode"
                value={formData.consultation_mode}
                onChange={handleChange}
                label="Modalidad"
                displayEmpty
              >
                <MenuItem value="" disabled>Elige una Modalidad</MenuItem>
                <MenuItem value="presencial">Presencial (Rivera, Uruguay)</MenuItem>
                <MenuItem value="online" disabled={formData.plan === "medidas"}>Online</MenuItem>
              </Select>
            </FormControl>
            </Grid>

            {formData.plan && formData.plan !== "medidas" && (
              <>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox checked={presencialCheck} onChange={handlePresencialCheck} color="primary" />
                    }
                    label="Quiero realizar las medidas antropométricas (solo presencial)"
                  />
                </Grid>
                <input 
                  type="hidden" 
                  name="anthropometric_measures" 
                  value={formData.anthropometric_measures} 
                />
              </>
            )}

            <Grid item xs={12}>
              <Button type="submit" variant="contained" color="primary" fullWidth size="large"
                sx={{ mt: 2, py: 1.5, fontSize: '1.1rem', '&:hover': { backgroundColor: 'primary.dark' } }}>
                Enviar
              </Button>
            </Grid>
          </Grid>
        </form>

        {/* Texto debajo de Campos Requeridos */}
        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
          <RequiredLabel>*</RequiredLabel> Campos Requeridos
        </Typography>
        {(formData.plan === "medidas" || presencialCheck) && (
          <Typography 
            variant="caption" 
            color="error" 
            sx={{ mt: 1, display: 'block', fontWeight: "bold" }}
          >
            ⚠️ Las medidas deben realizarse de forma presencial en Rivera, Uruguay
          </Typography>
        )}

        {/* Mensaje de confirmación o error */}
        {message.text && (
          <Box sx={{ bgcolor: message.type === 'success' ? 'success.main' : 'error.main', p: 2, borderRadius: 1, mt: 2 }}>
            <Typography variant="body2" color="white">{message.text}</Typography>
          </Box>
        )}
      </StyledPaper>
    </ThemeProvider>
  );
}
